import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import Background from "@/components/landing/Background";
import AppHeader from "@/components/nav/AppHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PLANS, CURRENCY, PRICING_ASSURANCE } from "@/lib/plans";

export default function Pricing() {
    const { user } = useAuth();
    const [annual, setAnnual] = useState(false);
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState("");

    // PR: delayed-paywall. Pricing can be reached from Step 3 with
    // ?next=generate_strategy. We pass that into /checkout/session as
    // pending_action so the backend can persist it on the user and
    // CheckoutSuccess knows to auto-resume after payment.
    const pendingAction = new URLSearchParams(window.location.search).get("next");

    const handleStart = async (planKey) => {
        setLoading(planKey);
        setError("");
        try {
            const r = await api.post("/checkout/session", {
                plan: planKey,
                billing: annual ? "annual" : "monthly",
                origin_url: window.location.origin,
                pending_action: pendingAction || undefined,
            });
            window.location.href = r.data.url;
        } catch (e) {
            setError(e?.response?.data?.detail || "Не вдалося створити платіж");
            setLoading(null);
        }
    };

    return (
        <div className="min-h-screen relative" data-testid="pricing-page">
            <Background />
            <div className="relative z-10">
                <AppHeader mode="public" />
                <div className="max-w-[1240px] mx-auto px-6 py-16">
                    <div className="text-center">
                        {user && (
                            <div className="text-xs font-mono text-secondary-c mb-3" data-testid="pricing-user-email">
                                Авторизовано як {user.email}
                            </div>
                        )}
                        <h1 className="font-display font-extrabold text-text-primary" style={{ fontSize: "clamp(36px, 5vw, 56px)", letterSpacing: "-0.025em" }}>
                            Оберіть ваш план
                        </h1>
                        <p className="mt-4 text-lg text-secondary-c">Через 20 хвилин реклама вже налаштована.</p>
                    </div>

                    <div className="mt-10 flex items-center justify-center gap-3">
                        <button onClick={() => setAnnual(false)} data-testid="toggle-monthly"
                            className={`px-5 py-2 rounded-full text-sm font-medium transition ${!annual ? "bg-surface-hover text-text-primary border border-primary/40" : "text-secondary-c"}`}>
                            Місяць
                        </button>
                        <button onClick={() => setAnnual(true)} data-testid="toggle-yearly"
                            className={`px-5 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${annual ? "bg-surface-hover text-text-primary border border-primary/40" : "text-secondary-c"}`}>
                            Рік
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-success/15 text-success-c">−25%</span>
                        </button>
                    </div>

                    {error && (
                        <div className="mt-6 text-center text-sm text-error-c font-mono" data-testid="pricing-error">{error}</div>
                    )}

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PLANS.map((p) => {
                            const price = annual ? p.annual : p.monthly;
                            return (
                                <div key={p.key} className={`relative glass rounded-2xl p-7 ${p.featured ? "scale-[1.02]" : ""}`}
                                    style={{ borderColor: p.featured ? "rgba(109,94,248,0.45)" : undefined, boxShadow: p.featured ? "0 0 32px rgba(109,94,248,0.18)" : undefined }}
                                    data-testid={`plan-card-${p.key}`}>
                                    {p.featured && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-medium text-white" style={{ background: "var(--gradient-1)" }}>Найпопулярніший</span>
                                    )}
                                    <h3 className="font-display font-bold text-2xl text-text-primary">{p.name}</h3>
                                    <p className="mt-1 text-sm text-secondary-c">{p.tagline}</p>
                                    <div className="mt-6 flex items-baseline gap-1.5 h-[64px]">
                                        <AnimatePresence mode="wait">
                                            <motion.div key={annual ? "a" : "m"} initial={{ rotateX: 90, opacity: 0 }} animate={{ rotateX: 0, opacity: 1 }} exit={{ rotateX: -90, opacity: 0 }} transition={{ duration: 0.4 }} className="flex items-baseline gap-1.5">
                                                <span className="font-display font-extrabold text-text-primary" style={{ fontSize: "52px", letterSpacing: "-0.03em" }}>{CURRENCY}{price}</span>
                                                <span className="text-secondary-c text-sm">/міс</span>
                                            </motion.div>
                                        </AnimatePresence>
                                    </div>
                                    <ul className="mt-6 space-y-3">
                                        {p.highlights.map((h, j) => (
                                            <li key={j} className="flex gap-2.5 items-start text-sm">
                                                <Check size={16} className={`mt-0.5 shrink-0 ${h.upgrade ? "text-primary-c" : "text-success-c"}`} />
                                                <span className="text-text-primary/90">{h.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => handleStart(p.key)}
                                        disabled={loading === p.key}
                                        data-testid={`select-plan-${p.key}`}
                                        className={`mt-7 w-full flex items-center justify-center gap-2 ${p.featured ? "btn-primary" : "btn-ghost"} ${loading === p.key ? "opacity-60 cursor-wait" : ""}`}
                                    >
                                        {loading === p.key && <Loader2 size={16} className="animate-spin" />}
                                        Почати з {p.name}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-10 text-center text-sm text-secondary-c">
                        {PRICING_ASSURANCE}
                    </p>
                </div>
            </div>
        </div>
    );
}